import { Component, OnDestroy, OnInit } from '@angular/core'
import { FormControl } from '@angular/forms'
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http'
import { MatDialog } from '@angular/material/dialog'
import { PageEvent } from '@angular/material/paginator'
import { Subject } from 'rxjs'
import { debounceTime, takeUntil } from 'rxjs/operators'
import { AdminPpa, AdminService, BlockedPpaName } from '../../services/admin.service'
import { ConfirmActionDialogComponent } from '../../dialogs/confirm-action-dialog/confirm-action-dialog.component'
import {
  AdminPpaRemoveDialogComponent,
  AdminPpaRemoveDialogData,
  AdminPpaRemoveDialogResult,
} from '../../dialogs/admin-ppa-remove-dialog/admin-ppa-remove-dialog.component'
import { ppaPlace } from '../../utils/ppa.util'
import { PPA_DELETION_CATEGORIES, ppaDeletionCategoryLabel, retentionText } from '../../utils/ppa-deletion.util'

/**
 * PPAs, the ones removed recently, and names nobody may add. Removing asks why (always): a
 * duplicate is merged into the PPA it duplicates, anything else is deleted - hidden everywhere at
 * once, its corp members told, and removed for good with its reviews after the retention period
 * (6 months), until then restorable from "Recently deleted".
 */
@Component({
  selector: 'app-admin-ppas',
  templateUrl: './admin-ppas.component.html',
  styleUrls: ['../admin-table.css', './admin-ppas.component.css'],
})
export class AdminPpasComponent implements OnInit, OnDestroy {
  readonly ppaPlace = ppaPlace
  readonly categoryLabel = ppaDeletionCategoryLabel
  readonly categories = PPA_DELETION_CATEGORIES
  readonly pageSizeOptions = [10, 20, 50, 100]
  readonly columns = ['name', 'place', 'reviews', 'corpers', 'actions']
  readonly deletedColumns = ['name', 'reason', 'deleted', 'purges', 'actions']
  readonly blockedColumns = ['name', 'reason', 'blocked', 'actions']

  searchControl = new FormControl('', { nonNullable: true })
  categoryControl = new FormControl('', { nonNullable: true })

  ppas: AdminPpa[] = []
  loading = false
  page = 1
  limit = 20
  total = 0

  deleted: AdminPpa[] = []
  loadingDeleted = false
  deletedPage = 1
  deletedLimit = 10
  totalDeleted = 0
  /** Every deleted PPA, by category - why PPAs get removed. */
  categoryCounts: Record<string, number> = {}
  /** How long a deleted PPA is kept - the backend's DELETED_PPA_RETENTION_DAYS. */
  retentionDays = 180

  blockedNames: BlockedPpaName[] = []
  loadingBlocked = false
  blockedPage = 1
  blockedLimit = 10
  totalBlocked = 0

  /** PPAs with a request in flight, so a second click doesn't send it twice. */
  busyIds = new Set<number>()
  /** Blocked names being unblocked. */
  busyBlockedIds = new Set<number>()

  private destroy$ = new Subject<void>()

  constructor(public adminService: AdminService, private dialog: MatDialog) {}

  ngOnInit(): void {
    this.loadPpas()
    this.loadDeleted()
    this.loadBlocked()
    this.searchControl.valueChanges.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(() => {
      this.page = 1
      this.loadPpas()
      this.deletedPage = 1
      this.loadDeleted()
    })
    this.categoryControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.deletedPage = 1
      this.loadDeleted()
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  get retention(): string {
    return retentionText(this.retentionDays)
  }

  /** "Duplicate (5)" - for the category filter. */
  categoryOption(category: { value: string; label: string }): string {
    return `${category.label} (${this.categoryCounts[category.value] ?? 0})`
  }

  private get search(): string | undefined {
    return this.searchControl.value.trim() || undefined
  }

  loadPpas(): void {
    this.loading = true
    this.adminService.getPpas({ page: this.page, limit: this.limit, search: this.search }).subscribe({
      next: (res) => {
        this.loading = false
        if (res.status === HttpStatusCode.Ok && res.body) {
          this.ppas = res.body.ppas ?? []
          this.total = res.body.total ?? this.ppas.length
        }
      },
      error: () => {
        this.loading = false
        this.adminService.showNotification('Failed to load PPAs', 5000, 'Close')
      },
    })
  }

  loadDeleted(): void {
    this.loadingDeleted = true
    const category = this.categoryControl.value || undefined
    this.adminService
      .getPpas({ page: this.deletedPage, limit: this.deletedLimit, search: this.search, deleted: 'only', category })
      .subscribe({
        next: (res) => {
          this.loadingDeleted = false
          if (res.status === HttpStatusCode.Ok && res.body) {
            this.deleted = res.body.ppas ?? []
            this.totalDeleted = res.body.total ?? this.deleted.length
            this.retentionDays = res.body.retention_days ?? this.retentionDays
            this.categoryCounts = res.body.category_counts ?? {}
          }
        },
        error: () => {
          this.loadingDeleted = false
          this.adminService.showNotification('Failed to load recently deleted PPAs', 5000, 'Close')
        },
      })
  }

  loadBlocked(): void {
    this.loadingBlocked = true
    this.adminService.getBlockedPpaNames({ page: this.blockedPage, limit: this.blockedLimit }).subscribe({
      next: (res) => {
        this.loadingBlocked = false
        if (res.status === HttpStatusCode.Ok && res.body) {
          this.blockedNames = res.body.blocked_names ?? []
          this.totalBlocked = res.body.total ?? this.blockedNames.length
        }
      },
      error: () => {
        this.loadingBlocked = false
        this.adminService.showNotification('Failed to load blocked names', 5000, 'Close')
      },
    })
  }

  onPageChange(event: PageEvent): void {
    this.limit = event.pageSize
    this.page = event.pageIndex + 1
    this.loadPpas()
  }

  onDeletedPageChange(event: PageEvent): void {
    this.deletedLimit = event.pageSize
    this.deletedPage = event.pageIndex + 1
    this.loadDeleted()
  }

  onBlockedPageChange(event: PageEvent): void {
    this.blockedLimit = event.pageSize
    this.blockedPage = event.pageIndex + 1
    this.loadBlocked()
  }

  private reloadAll(): void {
    this.loadPpas()
    this.loadDeleted()
    this.loadBlocked()
  }

  /** Merge it into the PPA it duplicates, or delete it - the dialog asks which, and why. */
  removePpa(ppa: AdminPpa): void {
    const ref = this.dialog.open<AdminPpaRemoveDialogComponent, AdminPpaRemoveDialogData, AdminPpaRemoveDialogResult>(
      AdminPpaRemoveDialogComponent,
      { width: '100%', maxWidth: '560px', data: { ppa, retentionDays: this.retentionDays }, autoFocus: false },
    )
    ref.afterClosed().subscribe((result) => {
      if (!result) return
      this.busyIds.add(ppa.id)
      const request =
        result.action === 'merge'
          ? this.adminService.mergePpa(ppa.id, { into_id: result.into.id, note: result.note })
          : this.adminService.deletePpa(ppa.id, { category: result.category, note: result.note, block_name: result.block_name })
      request.subscribe({
        next: () => {
          this.busyIds.delete(ppa.id)
          this.adminService.showNotification(
            result.action === 'merge' ? `Merged ${ppa.name} into ${result.into.name}` : `Deleted ${ppa.name}`,
            3000,
            'OK',
          )
          this.reloadAll()
        },
        error: (err: HttpErrorResponse) => {
          this.busyIds.delete(ppa.id)
          const fallback = result.action === 'merge' ? 'Failed to merge PPA' : 'Failed to delete PPA'
          this.adminService.showNotification(err?.error?.message ?? fallback, 5000, 'Close')
        },
      })
    })
  }

  /** Restores it - and if a PPA with its name is listed there now, asks first. */
  restorePpa(ppa: AdminPpa, force = false): void {
    this.busyIds.add(ppa.id)
    this.adminService.restorePpa(ppa.id, force).subscribe({
      next: (res) => {
        this.busyIds.delete(ppa.id)
        const unblocked = res.body?.unblocked ? ' - its name can be added again' : ''
        this.adminService.showNotification(`Restored ${ppa.name}${unblocked}`, 4000, 'OK')
        this.reloadAll()
      },
      error: (err: HttpErrorResponse) => {
        this.busyIds.delete(ppa.id)
        if (err?.error?.code === 'RESTORE_CONFLICT') {
          this.confirmRestoreAnyway(ppa, err.error.conflicts ?? [])
          return
        }
        this.adminService.showNotification(err?.error?.message ?? 'Failed to restore PPA', 5000, 'Close')
      },
    })
  }

  private confirmRestoreAnyway(ppa: AdminPpa, conflicts: AdminPpa[]): void {
    const ref = this.dialog.open(ConfirmActionDialogComponent, {
      width: '440px',
      data: {
        title: 'Restore anyway?',
        message:
          'A PPA with the same name was added there since it was deleted. Restoring it makes two - ' +
          'you can then merge one into the other.',
        highlight: conflicts.map((c) => [c.name, ppaPlace(c.Locations?.[0])].filter(Boolean).join(', ')).join('; '),
        confirmText: 'Restore anyway',
        confirmColor: 'primary',
      },
    })
    ref.afterClosed().subscribe((result) => {
      if (result?.confirmed) this.restorePpa(ppa, true)
    })
  }

  unblockName(blocked: BlockedPpaName): void {
    this.busyBlockedIds.add(blocked.id)
    this.adminService.unblockPpaName(blocked.id).subscribe({
      next: () => {
        this.busyBlockedIds.delete(blocked.id)
        this.adminService.showNotification(`${blocked.name} can be added again`, 3000, 'OK')
        this.loadBlocked()
      },
      error: (err: HttpErrorResponse) => {
        this.busyBlockedIds.delete(blocked.id)
        this.adminService.showNotification(err?.error?.message ?? 'Failed to unblock the name', 5000, 'Close')
      },
    })
  }
}
